"use client";

/**
 * The moving part of the 404 page (app/not-found.tsx). It renders nothing of its own: the whole brand block is
 * server-rendered inside it, so the page is complete without JavaScript. All this does is write four numbers on
 * the stage as CSS custom properties, once per animation frame:
 *
 *   --mx / --my  the pointer, from -1 to 1 across the viewport, eased toward its target so the brand follows the
 *                mouse softly and drifts back to centre when the mouse leaves the window;
 *   --dx / --dy  a slow wander, two sines per axis whose periods are not multiples of one another, so the brand
 *                keeps breathing on a phone, on a keyboard, with no pointer at all.
 *
 * app/not-found.css turns those four numbers into the parallax (a different amount of travel per plane) and the
 * tilt (perspective + rotateX / rotateY, 8° at most). Nothing here reads or writes layout: transform only.
 *
 * Off: reduced motion (nothing is attached, the stage keeps the zeroes the stylesheet declares), a hidden tab, and
 * a stage scrolled off screen. Pointer tracking also needs a real mouse — (hover: hover) and (pointer: fine).
 */

import { useEffect, useRef, type ReactNode } from "react";

/** Travel of the eased pointer toward its target, per frame at 60 fps. Small = a long, calm glide. */
const EASE = 0.06;
/** Periods of the four drift sines, ms. Deliberately not multiples of one another: the wander never repeats. */
const DRIFT = { x: [7300, 11700], y: [9100, 13300] } as const;

const clamp = (value: number) => Math.min(1, Math.max(-1, value));

export default function Stage({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const stage = ref.current;
    if (!stage) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const fine = window.matchMedia("(hover: hover) and (pointer: fine)");
    const target = { x: 0, y: 0 };
    const eased = { x: 0, y: 0 };
    let elapsed = 0; // drift time, advanced only while the loop runs: a paused tab does not jump on return
    let previous = 0;
    let frame = 0;
    let onScreen = true;

    const set = (name: string, value: number) => stage.style.setProperty(name, value.toFixed(4));

    const tick = (now: number) => {
      frame = requestAnimationFrame(tick);
      elapsed += Math.min(now - previous, 64); // a long gap (a stall) advances the drift by one frame, not by a leap
      previous = now;

      eased.x += (target.x - eased.x) * EASE;
      eased.y += (target.y - eased.y) * EASE;
      set("--mx", eased.x);
      set("--my", eased.y);
      set("--dx", (Math.sin(elapsed / DRIFT.x[0]) + Math.sin(elapsed / DRIFT.x[1])) / 2);
      set("--dy", (Math.sin(elapsed / DRIFT.y[0]) + Math.sin(elapsed / DRIFT.y[1])) / 2);
    };

    const start = () => {
      if (frame || !onScreen || document.hidden) return;
      previous = performance.now();
      frame = requestAnimationFrame(tick);
    };
    const stop = () => {
      cancelAnimationFrame(frame);
      frame = 0;
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!fine.matches || event.pointerType === "touch") return;
      target.x = clamp((event.clientX / window.innerWidth) * 2 - 1);
      target.y = clamp((event.clientY / window.innerHeight) * 2 - 1);
    };
    // The pointer left the window (or the window lost focus): the brand eases back to centre, it does not snap.
    const recentre = () => {
      target.x = 0;
      target.y = 0;
    };
    const onPointerOut = (event: PointerEvent) => {
      if (!event.relatedTarget) recentre();
    };
    const onVisibility = () => (document.hidden ? stop() : start());

    const observer =
      "IntersectionObserver" in window
        ? new IntersectionObserver(([entry]) => {
            onScreen = entry ? entry.isIntersecting : true;
            if (onScreen) start();
            else stop();
          })
        : null;
    observer?.observe(stage);

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    document.addEventListener("pointerout", onPointerOut);
    window.addEventListener("blur", recentre);
    document.addEventListener("visibilitychange", onVisibility);
    start();

    return () => {
      stop();
      observer?.disconnect();
      window.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerout", onPointerOut);
      window.removeEventListener("blur", recentre);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <div ref={ref} className="v2-404__stage">
      {children}
    </div>
  );
}
